import type { TimeEntryItem } from '@hygge/shared';

export interface TeamTimeGroup {
  name: string;
  totalSeconds: number;
  entries: TimeEntryItem[];
  /** Set on a project group. */
  color?: string;
  /** Set on a person group. */
  avatarUrl?: string | null;
}

/**
 * Team Time's cards: one per project (Project Time) or per person (Team Time),
 * heaviest first, each with its total and its entries in the order given.
 *
 * The totals are summed from `entries`, so they are only as right as the rows
 * handed in — the tab reads every page of the window for that reason. [HW-930]
 */
export function groupTeamTime(
  entries: readonly TimeEntryItem[],
  by: 'project' | 'employee',
  formatName: (name: { firstName: string; lastName: string }) => string,
): Array<[string, TeamTimeGroup]> {
  const map = new Map<string, TeamTimeGroup>();
  for (const e of entries) {
    const id = by === 'project' ? e.projectId : e.employeeId;
    const existing = map.get(id);
    if (existing) {
      existing.totalSeconds += e.duration;
      existing.entries.push(e);
    } else if (by === 'project') {
      map.set(id, { name: e.project.name, color: e.project.color, totalSeconds: e.duration, entries: [e] });
    } else {
      map.set(id, {
        name: formatName({ firstName: e.employee.firstName, lastName: e.employee.lastName }),
        avatarUrl: e.employee.avatarUrl,
        totalSeconds: e.duration,
        entries: [e],
      });
    }
  }
  return Array.from(map.entries()).sort((a, b) => b[1].totalSeconds - a[1].totalSeconds);
}
